import React, { useState } from "react";
import { FcViewDetails } from "react-icons/fc";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { SearchIcon, CountryIcon } from "../../../../assets/Icons";
import { IoChevronDownOutline } from "react-icons/io5";
import { Button } from "../../../../components/Reusable/Button";
import ProductPackages from "../../ProductPackage";
import {
  LabelInput,
  DownloadLinkInput,
} from "../../../../components/Reusable/Layout";
import { EditGamerIcon, DeleteIcon } from "../../../../assets/Icons";
import { faq } from "../../../../assets/DemoData";
import FAQModal from "../../Modal/FAQModal";
import EditFAQModal from "../../Modal/EditFAQModal";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  deleteGamecardPlan,
  fetchGamecardPlans,
  updateGamecardPlan,
} from "../../../../features/auth/store/gamecardPlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { formatCountries } from "../../../../components/Reusable/utils";
import { buildPlanStatusFormData } from "../../statusToggleUtils";
import { StatusSwitch } from "../../../../components/Reusable/ToggleSwitch";
import {
  GamecardTableProps,
  ProductSearchFilterProps,
  GamecardDetailViewProps,
} from "./types";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { GamecardPlan } from "../../../../features/auth/store/types/game-types";
import { Faq } from "../../types";
import { FaSortAmountUp, FaSortAmountDown, FaSort } from "react-icons/fa";
import { SortBy, SortOrder } from "../../../../features/auth/store/types/types";

export const GamecardTable: React.FC<GamecardTableProps> = ({
  gamecardPlans,
  statusFilter,
  searchTerm,
  onGamecardPlanClick,
  onDeleteClick,
  onEditClick,
  loading,
}) => {
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.gamecardPlans);
  const dispatch = useDispatch<AppDispatch>();
  const [updatingPlanIds, setUpdatingPlanIds] = useState<number[]>([]);
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(
        fetchGamecardPlans({
          url: nextPageUrl,
          status: statusFilter !== "" ? statusFilter : undefined,
          search: searchTerm !== "" ? searchTerm : undefined,
        }),
      );
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(
        fetchGamecardPlans({
          url: prevPageUrl,
          status: statusFilter !== "" ? statusFilter : undefined,
          search: searchTerm !== "" ? searchTerm : undefined,
        }),
      );
    }
  };

  const handleStatusToggle = async (
    gamecardPlan: (typeof gamecardPlans)[number],
  ) => {
    const nextStatus = gamecardPlan.status === "on" ? "off" : "on";

    setUpdatingPlanIds((prev) => [...prev, gamecardPlan.id]);

    try {
      await dispatch(
        updateGamecardPlan({
          id: gamecardPlan.id,
          data: buildPlanStatusFormData(gamecardPlan, nextStatus),
        }),
      ).unwrap();
      toast.success(
        `Plan ${nextStatus === "on" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update plan status");
    } finally {
      setUpdatingPlanIds((prev) => prev.filter((id) => id !== gamecardPlan.id));
    }
  };
  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto border border-[#FFFFFF1A] rounded-2xl">
        <table className="w-full min-w-[1400px] font-Inter border border-[#FFFFFF1A] rounded-2xl  cousor-pointer">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left bg-gray-100 dark:bg-[#FFFFFF1A]">
                Status
              </th>
              <th className="px-6 text-left">Provider</th>
              <th className="px-6 text-left">Sub category</th>
              <th className="px-6 text-left">Product Name</th>
              <th className="px-6 text-left">Price Currency</th>
              <th className="px-6 text-left">Price Type</th>
              <th className="px-6 text-left">Wallet Price</th>
              <th className="px-6 text-left">API Price</th>
              <th className="px-6 text-left">ATM Price</th>
              <th className="px-6 text-left">Point Bonus</th>
              <th className="px-6 text-left">Discount Offered</th>
              <th className="px-6 text-left">Referral Eligible</th>
              <th className="px-6 text-left">
                Referral Reward (Converted to DG Points)
              </th>
              <th className="px-6 text-left">Source</th>
              <th className="px-6 text-left">Code</th>
              <th className="px-6 text-left">Countries</th>
              <th className="px-6 text-left">Product Image</th>
              <th className="px-6 text-left bg-gray-100 dark:bg-[#FFFFFF1A]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm cursor-pointer">
            {loading ? (
              <tr className="border-b border-gray-200 h-[80px]">
                <td colSpan={18} className="px-6 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : gamecardPlans.length > 0 ? (
              gamecardPlans.map((gamecardPlan) => (
                <tr
                  key={gamecardPlan.id}
                  className="border-b border-gray-200 h-[50px] "
                >
                  <td className="px-6 py-4 align-middle">
                    <StatusSwitch
                      checked={gamecardPlan.status === "on"}
                      disabled={updatingPlanIds.includes(gamecardPlan.id)}
                      onChange={() => handleStatusToggle(gamecardPlan)}
                      label={`Toggle ${gamecardPlan.name || "plan"} status`}
                    />
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.category?.provider?.name ||
                      gamecardPlan.provider?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.category?.name ||
                      gamecardPlan.sub_category?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.name || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.price_currency?.code ||
                      gamecardPlan.price_currency?.name ||
                      gamecardPlan.price_currency?.symbol ||
                      gamecardPlan.price_currency_id ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.price_type || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle capitalize">
                    {gamecardPlan.wallet_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.api_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.atm_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.point_bonus ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.discount ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.referral_eligible ? "Yes" : "No"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.referral_amount == null
                      ? "-"
                      : gamecardPlan.price_type === "percent"
                        ? `${gamecardPlan.referral_amount}% of purchase`
                        : `${gamecardPlan.referral_amount} fixed (plan currency)`}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.source || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.code || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {formatCountries(gamecardPlan.countries)}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <img
                      src={gamecardPlan.image_url || "/default-product.png"}
                      alt={gamecardPlan.name || "product"}
                      className="size-[40px] object-cover rounded-lg"
                    />
                  </td>
                  <td className="px-6">
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        className="cursor-pointer"
                        onClick={() => onGamecardPlanClick(gamecardPlan)}
                      >
                        <FcViewDetails size="20" />
                      </button>
                      <button
                        className="text-blue-600"
                        onClick={() =>
                          onEditClick(gamecardPlan as GamecardPlan)
                        }
                      >
                        <FiEdit3 className="text-2xl" />
                      </button>
                      <button
                        className="text-red-600"
                        onClick={() => onDeleteClick(gamecardPlan.id)}
                      >
                        <MdOutlineDelete className="text-2xl" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={18} className="px-6 text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToPreviousPage}
          disabled={loading || currentPage === 1 || !prevPageUrl}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToNextPage}
          disabled={
            loading ||
            currentPage === totalPages ||
            totalPages === 0 ||
            !nextPageUrl
          }
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const GamecardSearchFilter: React.FC<ProductSearchFilterProps> = ({
  onStatusFilter,
  onCreateNewProduct,
  searchTerm,
  onSearchChange,
  categoryFilter,
  onCategoryFilter,
  countryFilter,
  onCountryFilter,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);
  const [showCategoryDropdown, setShowCategoryDropdown] =
    useState<boolean>(false);
  const [showSortByDropdown, setShowSortByDropdown] = useState<boolean>(false);
  const [showSortOrderDropdown, setShowSortOrderDropdown] =
    useState<boolean>(false);
  const [showCountryDropdown, setShowCountryDropdown] =
    useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const { gamecardCategories } = useSelector(
    (state: RootState) => state.gamecardCategories,
  );

  const { items: countries } = useSelector(
    (state: RootState) => state.countries,
  );

  const statusOptions = ["on", "off"];

  const sortFieldOptions: { label: string; value: SortBy }[] = [
    { label: "Name", value: "name" },
    { label: "Created Date", value: "created_at" },
    { label: "Code", value: "code" },
  ];

  const sortOrderOptions: {
    label: string;
    value: SortOrder;
    icon: React.ReactNode;
  }[] = [
    {
      label: "Ascending",
      value: "asc",
      icon: <FaSortAmountUp className="text-xs" />,
    },
    {
      label: "Descending",
      value: "desc",
      icon: <FaSortAmountDown className="text-xs" />,
    },
  ];

  const selectedCategoryObj = gamecardCategories?.find(
    (cat) => String(cat.id) === String(categoryFilter),
  );
  const selectedCountryObj = countries?.find(
    (c) => String(c.id) === String(countryFilter),
  );

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    onStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const clearStatusFilter = () => {
    setSelectedStatus(null);
    onStatusFilter("");
    setShowStatusDropdown(false);
  };

  const closeAllDropdowns = () => {
    setShowCountryDropdown(false);
    setShowCategoryDropdown(false);
    setShowStatusDropdown(false);
    setShowSortByDropdown(false);
    setShowSortOrderDropdown(false);
  };

  return (
    <div className="flex flex-col lg:flex-row lg:justify-between gap-4 mb-4 w-full">
      <div className="relative w-full lg:max-w-[500px] h-[48px]">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search for a product..."
          className="border p-2 pl-10 rounded-3xl w-full h-[48px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <SearchIcon />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
        {/* Country Filter */}
        <div className="relative flex-1 lg:w-auto">
          <button
            className="border p-2 bg-[#F4F4F5] w-full min-w-[100px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => {
              const state = showCountryDropdown;
              closeAllDropdowns();
              setShowCountryDropdown(!state);
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <CountryIcon />
              <p className="text-sm truncate">
                {selectedCountryObj?.name || "Country"}
              </p>
              <IoChevronDownOutline />
            </div>
          </button>
          {showCountryDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-full lg:w-[180px] max-h-60 overflow-y-auto bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {countries && countries.length > 0 ? (
                countries.map((c: any) => (
                  <div
                    key={c.id}
                    className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer text-sm dark:text-white"
                    onClick={() => {
                      onCountryFilter(String(c.id));
                      setShowCountryDropdown(false);
                    }}
                  >
                    {c.name || c.code}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No countries
                </div>
              )}
              {countryFilter && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t text-sm"
                  onClick={() => {
                    onCountryFilter("");
                    setShowCountryDropdown(false);
                  }}
                >
                  Clear Filter
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative flex-1 lg:w-auto">
          <button
            className="border p-2 bg-[#F4F4F5] w-full min-w-[100px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => {
              const state = showCategoryDropdown;
              closeAllDropdowns();
              setShowCategoryDropdown(!state);
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm truncate">
                {selectedCategoryObj?.name || "Category"}
              </p>
              <IoChevronDownOutline />
            </div>
          </button>
          {showCategoryDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-full lg:w-[180px] max-h-60 overflow-y-auto bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {gamecardCategories && gamecardCategories.length > 0 ? (
                gamecardCategories.map((cat: any) => (
                  <div
                    key={cat.id}
                    className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer text-sm dark:text-white"
                    onClick={() => {
                      onCategoryFilter(String(cat.id));
                      setShowCategoryDropdown(false);
                    }}
                  >
                    {cat.name}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No categories
                </div>
              )}
              {categoryFilter && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t text-sm"
                  onClick={() => {
                    onCategoryFilter("");
                    setShowCategoryDropdown(false);
                  }}
                >
                  Clear Filter
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status Filter */}
        <div className="relative flex-1 lg:w-auto">
          <button
            className="border p-2 bg-[#F4F4F5] w-full min-w-[100px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => {
              const state = showStatusDropdown;
              closeAllDropdowns();
              setShowStatusDropdown(!state);
            }}
          >
            <div className="flex items-center gap-2 justify-center">
              <p className="text-sm truncate">{selectedStatus || "Status"}</p>
              <IoChevronDownOutline />
            </div>
          </button>
          {showStatusDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-full lg:w-[150px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {statusOptions.map((st) => (
                <div
                  key={st}
                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer text-sm dark:text-white"
                  onClick={() => {
                    handleStatusSelect(st);
                  }}
                >
                  {st}
                </div>
              ))}
              {selectedStatus && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t text-sm"
                  onClick={clearStatusFilter}
                >
                  Clear Filter
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sort By Dropdown */}
        <div className="relative flex-1 sm:flex-initial">
          <button
            className="border p-2 bg-[#F4F4F5] w-full min-w-[110px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => {
              const state = showSortByDropdown;
              closeAllDropdowns();
              setShowSortByDropdown(!state);
            }}
          >
            <div className="flex items-center gap-2 justify-center">
              <FaSort className="text-sm shrink-0" />
              <p className="text-sm truncate">
                {sortBy
                  ? sortFieldOptions.find((o) => o.value === sortBy)?.label
                  : "Sort By"}
              </p>
              <IoChevronDownOutline />
            </div>
          </button>
          {showSortByDropdown && (
            <div className="absolute left-0 lg:left-auto lg:right-0 z-20 mt-1 w-full lg:w-[160px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {sortFieldOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer text-sm dark:text-white ${
                    sortBy === option.value ? "font-semibold text-blue-500" : ""
                  }`}
                  onClick={() => {
                    onSortByChange(option.value);
                    setShowSortByDropdown(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
              {sortBy && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t text-sm"
                  onClick={() => {
                    onSortByChange("");
                    setShowSortByDropdown(false);
                  }}
                >
                  Clear Sort
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sort Order Dropdown */}
        <div className="relative flex-1 sm:flex-initial">
          <button
            className="border p-2 bg-[#F4F4F5] w-full min-w-[110px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => {
              const state = showSortOrderDropdown;
              closeAllDropdowns();
              setShowSortOrderDropdown(!state);
            }}
          >
            <div className="flex items-center gap-2 justify-center">
              {sortOrder === "asc" ? (
                <FaSortAmountUp className="text-xs shrink-0" />
              ) : (
                <FaSortAmountDown className="text-xs shrink-0" />
              )}
              <p className="text-sm truncate">
                {sortOrder === "asc" ? "Asc" : "Desc"}
              </p>
              <IoChevronDownOutline />
            </div>
          </button>
          {showSortOrderDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-full lg:w-[150px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {sortOrderOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer text-sm dark:text-white flex items-center gap-2 ${
                    sortOrder === option.value
                      ? "font-semibold text-blue-500"
                      : ""
                  }`}
                  onClick={() => {
                    onSortOrderChange(option.value);
                    setShowSortOrderDropdown(false);
                  }}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          onClick={() => onCreateNewProduct("0")}
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-lg"
          className="w-full lg:w-auto md:max-w-[200px] h-12 px-6 flex items-center justify-center"
        >
          New Products
        </Button>
      </div>
    </div>
  );
};

export const GamecardDetailView: React.FC<GamecardDetailViewProps> = ({
  gamecardPlan,
  onBack,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Controlled values
  const [name, setName] = useState(gamecardPlan.name);
  const [message, setMessage] = useState(gamecardPlan.message);
  const [status, setStatus] = useState(gamecardPlan.status);

  const [toggle, setToggle] = useState<string>("Product Details");
  const tabItems = ["Product Details", "Product Packages"];
  const [fieldSearch, setFieldSearch] = useState("");
  const [countries, setCountries] = useState<string[]>(
    (gamecardPlan.countries ?? []).map((c: any) => String(c)),
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);
  const [isEditFAQModalOpen, setIsEditFAQModalOpen] = useState(false);
  const [faqToEdit, setFaqToEdit] = useState<Faq | undefined>(undefined);
  const [googlePlayLink, setGooglePlayLink] = useState("");
  const [appStoreLink, setAppStoreLink] = useState("");
  if (!gamecardPlan) return null;

  const handleAddField = () => {
    const trimmed = fieldSearch.trim();
    if (trimmed && !countries.includes(trimmed)) {
      setCountries([...countries, trimmed]);
      setFieldSearch("");
    }
  };

  const handleRemoveField = (index: number) => {
    const updated = [...countries];
    updated.splice(index, 1);
    setCountries(updated);
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await dispatch(
        updateGamecardPlan({
          id: gamecardPlan.id,
          data: {
            name,
            message,
            status,
            countries,
          },
        }),
      ).unwrap();

      toast("Game card plan updated successfully!");
    } catch (error) {
      toast("Failed to update game card plan.");
      console.error("Update error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteGamecardPlan(gamecardPlan.id)).unwrap();
      toast("Game card plan deleted successfully.");
      setShowDeleteModal(false);
      onBack(); // Navigate away
      onBack(); // Go back to the previous view
    } catch (error) {
      toast("Failed to delete game card plan.");
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusToggle = () => {
    setStatus((prev) => (prev === "on" ? "off" : "on"));
  };

  return (
    <div className="w-full font-satoshi">
      <div className="p-6">
        {/* Header Section */}
        <div className="bg-white mb-6">
          <div className="flex items-center p-6 gap-2">
            <button
              onClick={onBack}
              className="text-gray-600 w-8 h-8 flex items-center justify-center rounded-3xl bg-[#F4F4F5] dark:text-white"
            >
              ←
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
              {gamecardPlan.name}
            </h2>
            <span
              className={`px-4 py-1 rounded-3xl text-sm ${
                gamecardPlan.status === "on"
                  ? "bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6]"
                  : "bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]"
              }`}
            >
              {gamecardPlan.status === "on" ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Tab and Save Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 rounded bg-white p-6 gap-4">
          <div className="flex gap-2">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`py-[6px] px-[10px] h-[42px] text-xs flex items-center justify-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-gray-100 dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="bg-white border border-[#D0D5DD] h-[40px] rounded-lg text-sm font-semibold px-4 py-2"
          >
            {isUpdating ? "Saving..." : "Save changes"}
          </button>
        </div>

        {/* Main Content */}
        {toggle === "Product Details" ? (
          <>
            <div className="bg-white flex flex-col md:flex-row gap-6 p-4 mb-8">
              <div className="bg-[#E6F5FE] dark:bg-[#FFFFFF0A] p-6 rounded-lg flex-1">
                <div className="flex gap-6 justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total purchases of all products
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  1,000
                </div>
              </div>
              <div className="bg-[#F6FEF9] dark:bg-[#FFFFFF0A] p-6 rounded-lg flex-1">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total Revenue Generated
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  ₦5,000,000
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">
                    Product Details
                  </h3>
                  <div className="space-y-4">
                    <LabelInput
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Product Name"
                      name="Product Name"
                    />
                    <LabelInput
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Description"
                      name="Description"
                      className="h-[135px]"
                    />
                  </div>
                </div>

                <div className="bg-white rounded p-4">
                  <p className="text-lg text-[#012030] mb-6 font-semibold border-b border-[#D0D5DD] pb-2">
                    Product FAQ’s
                  </p>
                  {faq.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-center py-4 border-b border-[#D0D5DD]"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-xs">{item.question}</p>
                        <p className="text-[#475467] text-sm">{item.answer}</p>
                      </div>
                      <div className="flex gap-4">
                        <button
                          onClick={() => {
                            setIsEditFAQModalOpen(true);
                            setFaqToEdit(item);
                          }}
                        >
                          <EditGamerIcon />
                        </button>
                        <button>
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    className="flex items-center mt-4 gap-2 border border-[#D0D5DD] w-[149px] h-[36px] rounded text-sm font-semibold justify-center"
                    onClick={() => setIsFAQModalOpen(true)}
                  >
                    <span>+</span> Add New FAQ
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Images
                  </h3>
                  <img
                    src={gamecardPlan.image_url}
                    alt={gamecardPlan.name}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>

                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Availability
                  </h3>
                  <div className="flex justify-between items-center">
                    <span className="text-[#475467] text-base font-inter dark:text-gray-300">
                      Publish product
                    </span>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={status === "on"}
                        onChange={handleStatusToggle}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Links
                  </h3>
                  <div className="space-y-3">
                    <div className="text-gray-400 text-sm">Download Link</div>
                    <DownloadLinkInput
                      name="googlePlayLink"
                      value={googlePlayLink}
                      onChange={(e) => setGooglePlayLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="google"
                    />
                    <div className="text-gray-400 text-sm">Download Link</div>
                    <DownloadLinkInput
                      name="appStoreLink"
                      value={appStoreLink}
                      onChange={(e) => setAppStoreLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="apple"
                    />
                  </div>
                </div>

                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Countries
                  </h3>
                  <div className="flex gap-2 items-center mb-4">
                    <div className="relative w-full">
                      <input
                        type="text"
                        placeholder="Search for country"
                        value={fieldSearch}
                        onChange={(e) => setFieldSearch(e.target.value)}
                        className="w-full p-2 border rounded bg-white pl-10"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <SearchIcon />
                      </div>
                    </div>
                    <button
                      onClick={handleAddField}
                      className="w-[150px] font-semibold border border-[#D0D5DD] px-2 py-2 rounded-md text-sm"
                    >
                      Add Country
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {countries.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-4 bg-[#e5f1fa] text-sm px-3 py-2 rounded-full"
                      >
                        {item}
                        <button onClick={() => handleRemoveField(idx)}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="text-red-500 text-base w-[152px] h-[48px] bg-[#FEF3F2] font-medium rounded-lg"
              >
                Delete Product
              </button>
            </div>
          </>
        ) : (
          <ProductPackages productId={gamecardPlan.id} />
        )}
      </div>

      {/* Global Modals */}
      <FAQModal
        onCreate={(question, answer) => {
          console.log("New FAQ:", { question, answer });
        }}
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
      />
      <EditFAQModal
        onCreate={(question, answer) => {
          console.log("New FAQ:", { question, answer });
        }}
        isOpen={isEditFAQModalOpen && !!faqToEdit}
        onClose={() => {
          setIsEditFAQModalOpen(false);
          setFaqToEdit(undefined);
        }}
        faq={faqToEdit}
      />
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className="text-center space-y-4">
          <h2 className="text-lg font-semibold dark:text-white">
            Are you sure you want to delete this product?
          </h2>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 bg-gray-300 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
